import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Box, SimpleGrid, Flex } from "@chakra-ui/react";
import { BOOKING_API } from "./facilityData";

const GST_RATE = 0.18;

const INDIA_STATES = [
  "आंध्र प्रदेश","अरुणाचल प्रदेश","असम","बिहार","छत्तीसगढ़","गोवा","गुजरात","हरियाणा",
  "हिमाचल प्रदेश","झारखंड","कर्नाटक","केरल","मध्य प्रदेश","महाराष्ट्र","मणिपुर",
  "मेघालय","मिज़ोरम","नागालैंड","ओडिशा","पंजाब","राजस्थान","सिक्किम","तमिलनाडु",
  "तेलंगाना","त्रिपुरा","उत्तर प्रदेश","उत्तराखंड","पश्चिम बंगाल","दिल्ली",
];

function formatHindiDate(dateStr) {
  const d = new Date(dateStr + "T00:00:00");
  return d.toLocaleDateString("hi-IN", { weekday: "short", day: "2-digit", month: "long", year: "numeric" });
}

function buildPrintHTML(r) {
  return `<!DOCTYPE html><html><head><title>Booking Summary - ${r.bookingId}</title>
<style>
  body { font-family: Arial, sans-serif; padding: 32px; max-width: 680px; margin: 0 auto; color: #222; }
  h2 { color: #0047ab; margin-bottom: 4px; }
  .sub { color: #555; font-size: 0.9em; margin-bottom: 20px; }
  .box { background: #f0f7ff; border-radius: 8px; padding: 14px 20px; margin-bottom: 20px; }
  .bid { font-size: 1.4em; font-weight: 700; color: #0047ab; }
  .sec { background: #0047ab; color: #fff; padding: 8px 14px; font-weight: 700; border-radius: 4px 4px 0 0; margin-top: 16px; }
  table { width: 100%; border-collapse: collapse; }
  td { padding: 8px 10px; border-bottom: 1px solid #eee; font-size: 0.9em; }
  td:first-child { color: #666; width: 50%; }
  .total td { font-weight: 700; font-size: 1em; border-bottom: none; }
  @media print { body { padding: 0; } }
</style></head><body>
<h2>BSSA Ground Booking</h2>
<p class="sub">Bihar State Sports Authority — Patliputra Sports Complex</p>
<div class="box">
  <div style="font-size:0.85em;color:#555;">Registration Number</div>
  <div class="bid">${r.bookingId}</div>
  <div style="margin-top:8px;font-size:0.9em;">Payment Due: <strong>${r.paymentDueDate}</strong> (within 3 days)</div>
</div>
<div class="sec">Booking Details</div>
<table>
  <tr><td>Facility</td><td><strong>${r.facility.nameEn}</strong></td></tr>
  <tr><td>Date</td><td>${r.selectedDate}</td></tr>
  <tr><td>Time Slot</td><td>${r.selectedSlot}</td></tr>
  <tr><td>Duration</td><td>${r.durationHours} Hours</td></tr>
  <tr><td>Players</td><td>${r.numberOfPlayers}</td></tr>
  <tr><td>Capacity</td><td>${r.facility.capacity} players (approx)</td></tr>
</table>
<div class="sec">Payment Summary</div>
<table>
  <tr><td>Ground Booking Charges</td><td>₹${r.baseAmount}</td></tr>
  <tr><td>GST (18%)</td><td>₹${r.taxAmount}</td></tr>
  <tr class="total"><td>Total</td><td>₹${r.totalAmount}</td></tr>
</table>
<div class="sec">Customer Details</div>
<table>
  <tr><td>Name</td><td>${r.firstName} ${r.lastName}</td></tr>
  <tr><td>Phone</td><td>+91 ${r.phone}</td></tr>
  <tr><td>Email</td><td>${r.email}</td></tr>
  ${r.address ? `<tr><td>Address</td><td>${r.address}</td></tr>` : ""}
</table>
<p style="margin-top:24px;font-size:0.8em;color:#999;text-align:center;">Bihar State Sports Authority | This is an auto-generated summary.</p>
</body></html>`;
}

export default function GroundBookingForm() {
  const location = useLocation();
  const navigate = useNavigate();
  const state = location.state;

  // All hooks must be called unconditionally before any early return
  const [form, setForm] = useState({
    firstName: "", lastName: "", phone: "", email: "",
    address: "", country: "India", state: "", pinCode: "",
    numberOfPlayers: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError]           = useState("");
  const [result, setResult]         = useState(null);

  useEffect(() => {
    if (!state) navigate("/ground-booking");
  }, []); // eslint-disable-line

  if (!state) return null;

  const { facility, selectedDate, selectedSlot, startTime, endTime, durationHours } = state;
  const baseAmount  = facility.pricePerSlot;
  const taxAmount   = Math.round(baseAmount * GST_RATE);
  const totalAmount = baseAmount + taxAmount;

  const set = (field, val) => setForm(prev => ({ ...prev, [field]: val }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!/^\d{10}$/.test(form.phone)) { setError("फ़ोन नंबर 10 अंकों का होना चाहिए।"); return; }
    if (!form.numberOfPlayers || Number(form.numberOfPlayers) < 1) { setError("खिलाड़ियों की संख्या कम से कम 1 होनी चाहिए।"); return; }

    setSubmitting(true);
    try {
      const payload = {
        facilityId: facility.id, facilityName: facility.nameHi, facilityType: facility.type,
        bookingDate: selectedDate, timeSlot: selectedSlot, startTime, endTime, durationHours,
        numberOfPlayers: form.numberOfPlayers, baseAmount,
        ...form,
      };
      const res  = await fetch(`${BOOKING_API}/api/ground-booking`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (data.success) {
        setResult({
          ...data.data,
          facility, selectedDate, selectedSlot, startTime, endTime, durationHours,
          baseAmount, taxAmount: data.data.taxAmount, totalAmount: data.data.totalAmount,
          firstName: form.firstName, lastName: form.lastName,
          phone: form.phone, email: form.email, address: form.address,
          numberOfPlayers: form.numberOfPlayers,
        });
      } else {
        setError(data.error ? `${data.message} — ${data.error}` : data.message || "बुकिंग विफल हुई। कृपया पुनः प्रयास करें।");
      }
    } catch {
      setError("सर्वर से संपर्क नहीं हो सका। कृपया बाद में प्रयास करें।");
    } finally {
      setSubmitting(false);
    }
  };

  const downloadSummary = () => {
    const w = window.open("", "_blank");
    w.document.write(buildPrintHTML(result));
    w.document.close();
    setTimeout(() => w.print(), 400);
  };

  return (
    <Box maxW="1100px" mx="auto" px={{ base: 4, md: 5 }} py={{ base: 6, md: 8 }} fontSize="17px">
      <h1 style={{ color: "#0047ab", fontSize: "2rem", fontWeight: 700, marginBottom: 28 }}>बुकिंग की पुष्टि करें</h1>

      {/* Form + Sidebar grid — stacks on mobile, side-by-side on desktop */}
      <Box display="grid" gridTemplateColumns={{ base: "1fr", lg: "1fr 360px" }} gap={{ base: 8, lg: 9 }} alignItems="start">

        {/* ─── Left: Form ─── */}
        <form onSubmit={handleSubmit}>
          <SectionTitle>आपका विवरण</SectionTitle>

          <Row>
            <Field label="पहला नाम *"><input style={inp} value={form.firstName} onChange={e => set("firstName", e.target.value)} required /></Field>
            <Field label="अंतिम नाम *"><input style={inp} value={form.lastName}  onChange={e => set("lastName",  e.target.value)} required /></Field>
          </Row>
          <Row>
            <Field label="फ़ोन नंबर *"><input style={inp} type="tel"   value={form.phone} onChange={e => set("phone", e.target.value)} required /></Field>
            <Field label="ईमेल पता *"> <input style={inp} type="email" value={form.email} onChange={e => set("email", e.target.value)} required /></Field>
          </Row>

          <div style={{ marginBottom: 14 }}>
            <label style={lbl}>पता</label>
            <textarea style={{ ...inp, height: 72, resize: "vertical" }} value={form.address} onChange={e => set("address", e.target.value)} />
          </div>

          <Row cols={3}>
            <Field label="देश">
              <select style={inp} value={form.country} onChange={e => set("country", e.target.value)}>
                <option value="India">India</option>
              </select>
            </Field>
            <Field label="राज्य">
              <select style={inp} value={form.state} onChange={e => set("state", e.target.value)}>
                <option value="">-- चुनें --</option>
                {INDIA_STATES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </Field>
            <Field label="डाक (ज़िप) कोड">
              <input style={inp} value={form.pinCode} onChange={e => set("pinCode", e.target.value)} maxLength={6} />
            </Field>
          </Row>

          <Row>
            <Field label="मैदान का नाम *">
              <input style={{ ...inp, background: "#f5f5f5" }} value={facility.nameHi} readOnly />
            </Field>
            <Field label="तिथि *">
              <input style={{ ...inp, background: "#f5f5f5" }} value={selectedDate} readOnly />
            </Field>
          </Row>
          <Row>
            <Field label="समय स्लॉट *">
              <input style={{ ...inp, background: "#f5f5f5" }} value={selectedSlot} readOnly />
            </Field>
            <Field label="अवधि *">
              <input style={{ ...inp, background: "#f5f5f5" }} value={`${durationHours} Hours`} readOnly />
            </Field>
          </Row>
          <Row>
            <Field label="खिलाड़ियों की संख्या *">
              <input style={inp} type="number" min="1" max={facility.capacity} value={form.numberOfPlayers} onChange={e => set("numberOfPlayers", e.target.value)} required />
            </Field>
            <Field label="मूल्य *">
              <input style={{ ...inp, background: "#f5f5f5" }} value={`₹${baseAmount}`} readOnly />
            </Field>
          </Row>

          {error && <p style={{ color: "#dc3545", marginBottom: 12, fontSize: "16px" }}>{error}</p>}

          <button
            type="submit"
            disabled={submitting}
            style={{ width: "100%", background: "#0047ab", color: "#fff", border: "none", borderRadius: 8, padding: "14px", fontSize: "1.1rem", fontWeight: 700, cursor: submitting ? "not-allowed" : "pointer", opacity: submitting ? 0.75 : 1 }}
          >
            {submitting ? "प्रोसेसिंग..." : "बुक करें"}
          </button>
        </form>

        {/* ─── Right: Sidebar ─── */}
        <div style={{ border: "1px solid #e0e0e0", borderRadius: 10, overflow: "hidden", position: "sticky", top: 20 }}>
          <img src={facility.images[0]} alt="" style={{ width: "100%", height: 155, objectFit: "cover", display: "block" }} />
          <div style={{ padding: 16 }}>
            <div style={{ fontWeight: 700, fontSize: "16px", marginBottom: 2 }}>पाटलिपुत्र स्पोर्ट्स कॉम्प्लेक्स</div>
            <div style={{ color: "#f5a623", fontSize: "16px" }}>{"★".repeat(facility.rating)}</div>
            <div style={{ fontSize: "15px", color: "#555", marginTop: 4 }}>📍 {facility.location}</div>
            <span style={{ display: "inline-block", background: "#1a7f4e", color: "#fff", borderRadius: 4, padding: "3px 10px", fontSize: "14px", marginTop: 6, fontWeight: 600 }}>
              {facility.ratingScore} · {facility.reviewCount.toLocaleString()} समीक्षाएँ
            </span>
          </div>

          <div style={{ padding: "14px 16px", borderTop: "1px solid #f0f0f0" }}>
            <div style={{ fontWeight: 700, fontSize: "16px", marginBottom: 10 }}>आपकी बुकिंग का विवरण</div>
            <SimpleGrid columns={2} gap={2} fontSize="15px">
              <div>
                <div style={{ color: "#888", marginBottom: 2 }}>चेक-इन</div>
                <div style={{ fontWeight: 600, lineHeight: 1.4 }}>{formatHindiDate(selectedDate)}</div>
                <div style={{ color: "#666", fontSize: "14px" }}>{startTime} बजे से</div>
              </div>
              <div>
                <div style={{ color: "#888", marginBottom: 2 }}>चेक-आउट</div>
                <div style={{ fontWeight: 600, lineHeight: 1.4 }}>{formatHindiDate(selectedDate)}</div>
                <div style={{ color: "#666", fontSize: "14px" }}>{endTime} बजे तक</div>
              </div>
            </SimpleGrid>
          </div>

          <div style={{ padding: "14px 16px", borderTop: "1px solid #f0f0f0" }}>
            <div style={{ fontWeight: 700, fontSize: "16px", marginBottom: 10 }}>मूल्य सारांश</div>
            <PriceRow label="मूल्य" value={`₹${baseAmount}`} />
            <PriceRow label="GST (18%)" value={`₹${taxAmount}`} />
            <div style={{ borderTop: "1px solid #eee", paddingTop: 8, marginTop: 4, display: "flex", justifyContent: "space-between", fontWeight: 700, fontSize: "16px" }}>
              <span>कुल</span><span>₹{totalAmount}</span>
            </div>
          </div>
        </div>
      </Box>

      {/* ─── Success Modal ─── */}
      {result && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: "16px 12px" }}>
          <div style={{ background: "#fff", borderRadius: 12, maxWidth: 700, width: "100%", maxHeight: "92vh", overflowY: "auto" }}>

            {/* Modal header */}
            <div style={{ padding: "18px 20px", borderBottom: "1px solid #eee", display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
              <Flex gap={3} alignItems="flex-start">
                <div style={{ width: 42, height: 42, background: "#28a745", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: "1.4rem", flexShrink: 0 }}>✓</div>
                <div>
                  <div style={{ fontWeight: 700, fontSize: "1.2rem" }}>Booking Request Submitted!</div>
                  <div style={{ color: "#555", fontSize: "15px", marginTop: 2 }}>आपकी बुकिंग की पुष्टि सफलतापूर्वक हो गई है</div>
                  <div style={{ color: "#333", fontSize: "14px", marginTop: 4 }}>
                    Your booking request is successful! Please pay within 3 days to confirm your booking.<br/>
                    <span style={{ color: "#555" }}>आपका बुकिंग अनुरोध सफल है! बुकिंग की पुष्टि के लिए कृपया 3 दिनों के भीतर भुगतान करें</span>
                  </div>
                </div>
              </Flex>
              <button onClick={() => { setResult(null); navigate("/ground-booking"); }} style={{ background: "none", border: "none", fontSize: "1.5rem", cursor: "pointer", color: "#666", lineHeight: 1, flexShrink: 0 }}>✕</button>
            </div>

            <div style={{ padding: "16px 20px" }}>
              {/* Booking ID + due date */}
              <SimpleGrid columns={{ base: 1, sm: 2 }} gap={4} background="#f8f9fa" borderRadius="8px" p="14px 16px" mb={4}>
                <div>
                  <div style={{ color: "#666", fontSize: "14px", marginBottom: 4 }}>Registration Number / पंजीकरण संख्या</div>
                  <div style={{ fontWeight: 700, color: "#0047ab", fontSize: "17px", wordBreak: "break-all" }}>{result.bookingId}</div>
                </div>
                <div>
                  <div style={{ color: "#666", fontSize: "14px", marginBottom: 4 }}>Payment Due Within / भुगतान की अंतिम तिथि</div>
                  <div style={{ fontWeight: 700, fontSize: "16px" }}>3 Days / {result.paymentDueDate}</div>
                </div>
              </SimpleGrid>

              <Flex justifyContent="space-between" flexWrap="wrap" gap={2} fontSize="15px" color="#555" mb={4}>
                <span>Booking Domain / बुकिंग डोमेन: <strong>Sports Booking</strong></span>
                <span>Due Date: <strong>{result.paymentDueDate}</strong></span>
              </Flex>

              {/* Booking Details */}
              <ModalSection title="Booking Details / बुकिंग विवरण">
                <Flex gap={3} flexWrap={{ base: "wrap", sm: "nowrap" }}>
                  <img src={facility.images[0]} alt="" style={{ width: 100, height: 70, objectFit: "cover", borderRadius: 6, flexShrink: 0 }} />
                  <div style={{ flex: 1, fontSize: "14px", minWidth: 130 }}>
                    <div style={{ fontWeight: 700 }}>Patliputra Sports Complex</div>
                    <div style={{ color: "#555" }}>📍 Kankarbagh, Patna</div>
                    <div>Ground: <strong>{facility.nameEn}</strong></div>
                    <div>Date: <strong>{selectedDate}, {startTime}</strong></div>
                    <div>End Time: <strong>{endTime}</strong></div>
                  </div>
                  <div style={{ fontSize: "14px", minWidth: 130 }}>
                    <div>Duration: <strong>{durationHours * 60} Minutes</strong></div>
                    <div>Capacity: <strong>{facility.capacity} Players (approx)</strong></div>
                    <div>Booking Date: <strong>{new Date().toLocaleDateString("en-IN")}</strong></div>
                  </div>
                </Flex>
              </ModalSection>

              {/* Payment Summary */}
              <ModalSection title="Payment Summary / भुगतान सारांश">
                <ModalRow label="Ground Booking Charges / ग्राउंड बुकिंग शुल्क" value={`₹${baseAmount}`} />
                <ModalRow label="Tax (18% GST on charges) / कर" value={`₹${result.taxAmount}`} />
                <div style={{ display: "flex", justifyContent: "space-between", padding: "10px 16px", fontWeight: 700, fontSize: "16px" }}>
                  <span>Total / कुल</span><span>₹{result.totalAmount}</span>
                </div>
              </ModalSection>

              {/* Customer + Additional info */}
              <SimpleGrid columns={{ base: 1, sm: 2 }} gap={4} mb={5} fontSize="15px">
                <div>
                  <div style={{ fontWeight: 700, marginBottom: 6 }}>Customer Details / ग्राहक विवरण</div>
                  <div>Name: <strong>{result.firstName} {result.lastName}</strong></div>
                  <div>Phone: <strong>+91 {result.phone}</strong></div>
                  <div>Email: <strong>{result.email}</strong></div>
                </div>
                <div>
                  <div style={{ fontWeight: 700, marginBottom: 6 }}>Additional Information / अतिरिक्त जानकारी</div>
                  <div style={{ color: "#555" }}>Special Request: {result.address || "—"}</div>
                  <div style={{ color: "#555" }}>IP Address: {result.ipAddress}</div>
                </div>
              </SimpleGrid>

              {/* Actions */}
              <Flex gap={3} justifyContent={{ base: "stretch", sm: "flex-end" }} flexDirection={{ base: "column", sm: "row" }}>
                <button
                  onClick={() => { setResult(null); navigate("/ground-booking"); }}
                  style={{ padding: "10px 24px", border: "1px solid #ccc", borderRadius: 6, background: "#fff", cursor: "pointer", fontWeight: 600 }}
                >
                  Close / बंद करें
                </button>
                <button
                  onClick={downloadSummary}
                  style={{ padding: "10px 24px", background: "#0047ab", color: "#fff", border: "none", borderRadius: 6, cursor: "pointer", fontWeight: 600 }}
                >
                  ⬇ Download Summary / सारांश डाउनलोड करें
                </button>
              </Flex>
            </div>
          </div>
        </div>
      )}
    </Box>
  );
}

// ─── Small helper components ───

function SectionTitle({ children }) {
  return <h3 style={{ fontSize: "16px", fontWeight: 700, borderBottom: "1px solid #eee", paddingBottom: 8, marginBottom: 16, marginTop: 8 }}>{children}</h3>;
}

function Row({ children, cols = 2 }) {
  return (
    <SimpleGrid columns={{ base: 1, sm: cols }} gap={3} mb="14px">
      {children}
    </SimpleGrid>
  );
}

function Field({ label, children }) {
  return <div><label style={lbl}>{label}</label>{children}</div>;
}

function PriceRow({ label, value }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6, fontSize: "14px" }}>
      <span style={{ color: "#555" }}>{label}</span><span>{value}</span>
    </div>
  );
}

function ModalSection({ title, children }) {
  return (
    <div style={{ borderRadius: 6, overflow: "hidden", marginBottom: 14 }}>
      <div style={{ background: "#0047ab", color: "#fff", padding: "10px 16px", fontWeight: 700, fontSize: "16px" }}>{title}</div>
      <div style={{ border: "1px solid #e0e0e0", borderTop: "none", padding: 14 }}>{children}</div>
    </div>
  );
}

function ModalRow({ label, value }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", padding: "10px 16px", borderBottom: "1px solid #f0f0f0", fontSize: "15px" }}>
      <span>{label}</span><span>{value}</span>
    </div>
  );
}

const lbl = { display: "block", fontSize: "15px", fontWeight: 600, color: "#333", marginBottom: 6 };
const inp = { width: "100%", border: "1px solid #ccc", borderRadius: 6, padding: "11px 14px", fontSize: "15px", outline: "none", boxSizing: "border-box" };
